import { chunk } from 'lodash';
import { useLayoutEffect, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import { useColumnCount } from './hooks/useColumnCount.hook';
import type { CardSize } from './types/card-size.type';

interface Props {
  readonly cards: readonly ReactNode[];
  readonly size: CardSize;
}

const ESTIMATED_ROW_HEIGHT_PX = 420;
const OVERSCAN_ROWS = 2;

// Only the rows of cards around the viewport are rendered (a thousand folders render as fast as
// ten); the rows are measured once shown, the cards having various heights. The page keeps
// scrolling as a whole.
export const VirtualCardRows = ({ cards, size }: Readonly<Props>) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [listTopPx, setListTopPx] = useState(0);
  const columnCount = useColumnCount(size);
  const rows = chunk(cards, columnCount);

  useLayoutEffect(() => {
    setListTopPx(
      (listRef.current?.getBoundingClientRect().top ?? 0) + window.scrollY,
    );
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    overscan: OVERSCAN_ROWS,
    scrollMargin: listTopPx,
  });

  return (
    <Box
      ref={listRef}
      sx={{ height: virtualizer.getTotalSize(), position: 'relative' }}
    >
      {virtualizer.getVirtualItems().map(({ index, key, start }) => (
        <Box
          data-index={index}
          key={key}
          ref={virtualizer.measureElement}
          sx={{
            left: 0,
            pb: 2,
            position: 'absolute',
            top: 0,
            transform: `translateY(${String(start - listTopPx)}px)`,
            width: '100%',
          }}
        >
          <Grid container spacing={2}>
            {rows[index]}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
